import { PreferencesState } from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { v4 as uuid } from 'uuid';
import { FormRowDirective } from '../../directives/form-row.directive';
import { DecimalPipe } from '../../pipes/decimal.pipe';
import { shortNumber } from '../../utilities/short-number.util';
import { ButtonGroup } from '../button-group/button-group.component';
import { Button } from '../button/button.component';
import { Card } from '../card/card.component';
import { FilterItem } from '../filter-item/filter-item.component';
import { InputRange } from '../input-range/input-range.component';
import { InputText } from '../input-text/input-text.component';
import { ShowField } from '../show-field/show-field.component';

type RangeGroup = FormGroup<{
  min: FormControl<number | null>;
  max: FormControl<number | null>;
}>;

const initialValues = {
  min: null,
  max: null,
};

@Component({
  selector: 'procure-filter-number-range',
  templateUrl: './filter-number-range.component.html',
  styleUrl: './filter-number-range.component.scss',
  imports: [
    ReactiveFormsModule,
    InputText,
    Button,
    FormRowDirective,
    ButtonGroup,
    MatIcon,
    ScrollingModule,
    Card,
    ShowField,
    InputRange,
    MatTooltip,
    DecimalPipe,
    NgTemplateOutlet,
  ],
  providers: [{ provide: FilterItem, useExisting: FilterNumberRange }],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FilterNumberRange extends FilterItem {
  readonly container =
    viewChild.required<ElementRef<HTMLDivElement>>('container');

  readonly outletRef = viewChild('outlet', { read: ViewContainerRef });

  readonly inputRangeRef = viewChild('inputRange', { read: TemplateRef });

  readonly group = input.required<RangeGroup>();
  readonly hint = input<string>('');
  readonly label = input.required<string>();
  readonly minValue = input<number>(0);
  readonly maxValue = input<number>(1000000);

  readonly decimal = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly overlay = viewChild<ElementRef>('overlay');

  readonly tooltip = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly primary = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  protected readonly activeColor = computed(() =>
    this.primary() ? 'primary' : 'black',
  );

  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly max = signal<number | null>(null);
  protected readonly min = signal<number | null>(null);

  protected readonly hasValue = computed(
    () => this.min() !== null && this.max(),
  );

  protected readonly isOpened = signal(false);
  protected readonly id = uuid();
  protected readonly alignment = signal('left');
  protected readonly dateFormat = inject(PreferencesState).dateFormat;

  protected readonly remainder = computed(() => {
    if (!this.hasValue()) {
      return null;
    }

    return `${shortNumber(this.min())} - ${shortNumber(this.max())}`;
  });

  private ignoreClick: boolean = true;
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();

    this.onGlobalClick = this.onGlobalClick.bind(this);

    effect(() => {
      const group = this.group();
      const { value } = group;

      if (!value) {
        return;
      }

      group.controls.min.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((min) => {
          this.min.set(min);
        });

      group.controls.max.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((max) => {
          this.max.set(max);
        });

      group.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.max.set(group.controls.max.value);
          this.min.set(group.controls.min.value);
        });
    });

    effect((cleanup) => {
      if (this.isOpened()) {
        this.checkPosition();
        document.addEventListener('click', this.onGlobalClick);
      } else {
        document.removeEventListener('click', this.onGlobalClick);
      }

      cleanup(() => {
        document.removeEventListener('click', this.onGlobalClick);
      });
    });
  }

  protected rerender(): void {
    const inputRangeRef = this.inputRangeRef();
    const outletRef = this.outletRef();

    if (!inputRangeRef || !outletRef) {
      return;
    }

    outletRef.clear();
    outletRef.createEmbeddedView(inputRangeRef);
  }

  protected toggleOverlay(): void {
    this.isOpened.update((value) => !value);
  }

  protected reset(): void {
    this.group().setValue(initialValues);
    this.rerender();
  }

  private onGlobalClick(event: MouseEvent): void {
    if (this.ignoreClick) {
      this.ignoreClick = false;
      return;
    }

    let element = event.target as HTMLElement;

    while (element && element.id !== this.id) {
      element = element.parentElement as HTMLElement;
    }

    if (element) {
      return;
    }

    this.isOpened.set(false);
    document.removeEventListener('click', this.onGlobalClick);
  }

  private checkPosition(): void {
    const { nativeElement } = this.container();

    const { left } = nativeElement.getBoundingClientRect();
    const width = Math.max(nativeElement.offsetWidth, 320);

    this.alignment.set(
      left + width > document.body.offsetWidth ? 'right' : 'left',
    );
  }
}
